import React, { useState } from 'react';
import { reportUser } from '../../services/chatServices';

/**
 * Shared "Report a user" modal. Extracted from ChatInterface so the same flow
 * can be triggered from anywhere a user is shown (chat, match cards, Discover
 * profiles). Calls POST /chat/report via reportUser(); conversationId is
 * optional, so it works with or without an existing conversation.
 *
 * Surfaces the backend's abuse-guard messages (already reported / too many
 * reports / self-report) inline instead of throwing an unhandled rejection.
 */

interface ReportUserModalProps {
  open: boolean;
  reportedUserId?: string;
  reportedName?: string;
  conversationId?: string;
  // true once the two users are connected (mutual approval / chat exists), so
  // interaction-based reasons apply. false (default) before any contact is
  // possible (pending match, Discover profile), where only the profile can be
  // judged, so messaging-based reasons are hidden.
  connected?: boolean;
  onClose: () => void;
}

// Pre-connection: no messages or requests are possible yet, so only the profile
// itself can be reported.
const PRE_CONNECTION_REASONS = [
  'Fake profile',
  'Inappropriate profile content',
  'Scam or misleading profile',
  'Other',
];

// Connected: the two users can message each other, so interaction-based reasons
// apply.
const CONNECTED_REASONS = [
  'Harassment',
  'Inappropriate messages',
  'Spam',
  'Scam or fraud',
  'Fake profile',
  'Other',
];

const ReportUserModal = ({
  open,
  reportedUserId,
  reportedName,
  conversationId,
  connected = false,
  onClose,
}: ReportUserModalProps) => {
  const reasons = connected ? CONNECTED_REASONS : PRE_CONNECTION_REASONS;
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!open) return null;

  const close = () => {
    setReason('');
    setError('');
    setDone(false);
    setSubmitting(false);
    onClose();
  };

  const submit = async () => {
    if (!reportedUserId || !reason || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await reportUser(reportedUserId, reason, '', conversationId);
      setDone(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(
        typeof msg === 'string' && msg
          ? msg
          : 'Could not submit the report. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center z-[100]'
      onClick={close}
    >
      <div
        className='bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-xl'
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>
              Report submitted
            </h3>
            <p className='text-sm text-gray-500 mb-6'>
              Thanks. Our team will review it.
            </p>
            <div className='flex justify-end'>
              <button
                onClick={close}
                className='px-5 py-2 bg-primary-600 text-white rounded-full text-sm font-medium hover:bg-primary-700'
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>
              Report{reportedName ? ` ${reportedName}` : ' user'}
            </h3>
            <p className='text-sm text-gray-500 mb-4'>
              Why are you reporting this user?
            </p>
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError('');
              }}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 outline-none focus:ring-2 focus:ring-primary-200'
            >
              <option value=''>Select a reason</option>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {error && <p className='text-sm text-red-600 mb-3'>{error}</p>}
            <div className='flex justify-end gap-3'>
              <button
                onClick={close}
                className='px-4 py-2 text-sm text-primary-600 hover:bg-gray-50 rounded-lg font-medium'
              >
                Cancel
              </button>
              <button
                disabled={!reason || submitting}
                onClick={submit}
                className='px-5 py-2 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 disabled:opacity-40'
              >
                {submitting ? 'Reporting...' : 'Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportUserModal;
